import { cloneDeep } from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Checkbox, Dropdown, Icon, Label, Popup } from "semantic-ui-react";
import { heightSet } from "../../../utilities/heightSet";
import { editVtype, fetchVtype, saveVtypeEditedRes, updateVtypeLoc } from "../data/actions";
import { searchColumns } from "../data/model";
import {
    getEditedRes,
    getFilteredVtype,
} from "../data/selectors";
import { fetchSegmap } from "../../segmap/data/actions";
import { getFilteredSegmap } from "../../segmap/data/selectors";
import { TableListDiv } from "./tableListDiv";

// The API may send app_only_4_loc_cd_lst as "" (empty), a JSON string, a
// comma-separated string, or an array. Always normalize to a string array.
const toLocArray = (v) => {
    if (Array.isArray(v)) return v;
    if (typeof v === "string") {
        const s = v.trim();
        if (!s) return [];
        if (s.startsWith("[")) {
            try {
                const parsed = JSON.parse(s);
                return Array.isArray(parsed) ? parsed.map(String) : [];
            } catch (e) {
                return [];
            }
        }
        return s.split(",").map((x) => x.trim()).filter(Boolean);
    }
    return [];
};

const VtypeList = (props) => {
    const dispatch = useDispatch();
    const vtype = useSelector((state) => getFilteredVtype(state, props));
    const editedRes = useSelector((state) => getEditedRes(state));
    const segmapData = useSelector((state) => getFilteredSegmap(state));

    const [heights, setHeight] = useState("");

    // Keep latest list available to memoized cell handlers without re-creating columns
    const vtypeRef = useRef(vtype);
    vtypeRef.current = vtype;

    // Distinct LOC CD values from the segmap list -> options for the LOC dropdown
    const locCdOptions = React.useMemo(() => {
        const set = new Set();
        (segmapData || []).forEach((s) => {
            const v = s && s.loc_cd;
            if (v !== null && v !== undefined && String(v).trim() !== "" && v !== "NULL") {
                set.add(String(v));
            }
        });
        return Array.from(set).sort().map((v) => ({ key: v, text: v, value: v }));
    }, [segmapData]);
    const locOptionsRef = useRef(locCdOptions);
    locOptionsRef.current = locCdOptions;

    useEffect(() => {
        dispatch(fetchVtype());
        dispatch(fetchSegmap());
    }, [dispatch]);

    useEffect(() => {
        if (editedRes) {
            if (parseInt(editedRes.status, 10) === 1) {
                toast.success("Real Time Posting enabled for " + editedRes.trans_type_name);
                dispatch(saveVtypeEditedRes({}));
            }
            if (parseInt(editedRes.status, 10) === 2) {
                toast.success("Park & Post enabled for " + editedRes.trans_type_name);
                dispatch(saveVtypeEditedRes({}));
            }
            if (parseInt(editedRes.status, 10) === 0 && editedRes.trans_type_name) {
                toast.error("Disabled " + editedRes.trans_type_name);
                dispatch(saveVtypeEditedRes({}));
            }
        }
    }, [editedRes]);

    const callHeightFunc = () => {
        heightSet(setHeight);
    };

    useEffect(() => {
        callHeightFunc();
        window.addEventListener(
            "resize",
            function () {
                callHeightFunc();
            },
            false
        );
        // Cleanup function to remove event listener when component unmounts
        return () => {
            window.removeEventListener("resize", callHeightFunc);
        };
    }, []);

    // status: 0 = disabled, 1 = Real Time Posting, 2 = Park and Post (mutually exclusive)
    const applyStatus = (vtypeGet, newStatus) => {
        vtypeGet = cloneDeep(vtypeGet);
        vtypeGet.status = newStatus;
        // VD and VP cannot both be enabled at the same time
        if (newStatus !== 0) { 
            const fulldata = cloneDeep(vtypeRef.current);
            const pairType =
                vtypeGet.trans_type === "VD"
                    ? "VP"
                    : vtypeGet.trans_type === "VP"
                        ? "VD"
                        : null;
            if (pairType) {
                const pairObj = fulldata.filter((item) => item.trans_type === pairType);
                if (pairObj.length > 0) {
                    let postPairObj = cloneDeep(pairObj[0]);
                    if (parseInt(postPairObj.status, 10) !== 0) {
                        postPairObj.status = 0;
                        dispatch(editVtype(postPairObj));
                    }
                }
            }
        }
        dispatch(editVtype(vtypeGet));
    };

    const toggleRealTimePosting = (item) => {
        applyStatus(item, parseInt(item.status, 10) === 1 ? 0 : 1);
    };

    const toggleParkAndPost = (item) => {
        applyStatus(item, parseInt(item.status, 10) === 2 ? 0 : 2);
    };

    // Make sure already-selected LOC values still render even if not in the list
    const buildLocOptions = (selected) => {
        const opts = locOptionsRef.current;
        const present = new Set(opts.map((o) => o.value));
        const list = Array.isArray(selected) ? selected : [];
        const extra = list
            .filter((v) => !present.has(v))
            .map((v) => ({ key: v, text: v, value: v }));
        return extra.length ? [...opts, ...extra] : opts;
    };

    const handleLocChange = (item, value) => {
        // Persist as a comma-separated string (e.g. "HA1,HA2"), not an array
        const locStr = (value || []).join(",");
        dispatch(updateVtypeLoc({ ...cloneDeep(item), app_only_4_loc_cd_lst: locStr }));
    };

    const columns = React.useMemo(
        () => [
            ...searchColumns,
            {
                Header: (
                    <div style={{ textAlign: "center", lineHeight: 1.25 }}>
                        Real Time Posting
                        <br />
                        Enable/Disable
                    </div>
                ),
                accessor: "status",
                id: "rtp_status",
                Cell: ({ row }) => {
                    const item = row.original;
                    return (
                        <div style={{ textAlign: "center" }}>
                            <Checkbox
                                toggle
                                checked={parseInt(item.status, 10) === 1}
                                onClick={() => toggleRealTimePosting(item)}
                            />
                        </div>
                    );
                },
                classNameGet: "vtypefilter3",
            },
            {
                Header: (
                    <div style={{ textAlign: "center", lineHeight: 1.25 }}>
                        Park and Post
                        <br />
                        Enable/Disable
                    </div>
                ),
                accessor: "status",
                id: "pnp_status",
                Cell: ({ row }) => {
                    const item = row.original;
                    return (
                        <div style={{ textAlign: "center" }}>
                            <Checkbox
                                toggle
                                checked={parseInt(item.status, 10) === 2}
                                onClick={() => toggleParkAndPost(item)}
                            />
                        </div>
                    );
                },
                classNameGet: "vtypefilter4",
            },
            {
                Header: <div style={{ textAlign: "left" }}>LOC CD</div>,
                accessor: "app_only_4_loc_cd_lst",
                id: "loc_cd_list",
                Cell: ({ row }) => {
                    const item = row.original;
                    const selected = toLocArray(item.app_only_4_loc_cd_lst);
                    return (
                        <div
                            style={{ display: "flex", alignItems: "center", gap: "6px", padding: "0 6px" }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <Dropdown
                                    className="vtypeLocDropdown"
                                    placeholder="Select LOC"
                                    fluid
                                    multiple
                                    selection
                                    search
                                    clearable
                                    options={buildLocOptions(selected)}
                                    value={selected}
                                    onChange={(e, { value }) => handleLocChange(item, value)}
                                />
                            </div>
                            <Popup
                                on="click"
                                position="left center"
                                hoverable
                                trigger={
                                    <Icon
                                        name="eye"
                                        link
                                        title="View selected LOC"
                                        style={{ color: selected.length ? "#2185d0" : "#c3ccd6", margin: 0 }}
                                    />
                                }
                                content={
                                    selected.length ? (
                                        <div style={{ maxWidth: "260px", display: "flex", flexWrap: "wrap", gap: "4px" }}>
                                            {selected.map((v) => (
                                                <Label key={v} size="tiny">{v}</Label>
                                            ))}
                                        </div>
                                    ) : (
                                        "No LOC selected"
                                    )
                                }
                            />
                        </div>
                    );
                },
                classNameGet: "vtypefilter5",
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    return (
        <>
            <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "10px 4px 12px",
            }}>
                <div>
                    <div style={{ fontSize: "17px", fontWeight: 600, color: "#1f2d3d", lineHeight: 1.2 }}>
                        Vtype Map
                    </div>
                    <div style={{ fontSize: "12px", color: "#8a96a8", marginTop: "2px" }}>
                        Enable or disable transaction types.
                    </div>
                </div>
            </div>
            <div className="" style={{ paddingTop: "10px" }}>
                <TableListDiv
                    columns={columns}
                    data={vtype}
                    setHeight={parseFloat(heights) - 180}
                    getIndex={99}
                    filterBoxNone={"3,4,5,"}
                    rightBox={""}
                    centerBox={""}
                    callHeightFunc={callHeightFunc}
                    paddingShort={2}
                    getWidth={"100%"}
                    actionClass={"vtypeAction"}
                />
            </div>
        </>
    );
};

export default VtypeList;
